import { ThymeFeatureFlagClient } from '@thyme/libs/src/flags/client'
import { FeatureFlagValues, FlagDefinition } from '@thyme/libs/src/flags/types'
import { BannerStatus } from '@/legacy/types/bannerStatus'

// TYPES --------------------
/**
 * Feature flag definitions and supporting types. Add new feature flags in this
 * module.
 * @module
 */

export type InfoBannerConfig = {
  message?: string
  status?: BannerStatus
}

export type ApiUrlOverrideConfig = {
  url?: string
}

export type ProviderConfig = {
  provider?: string
}

/**
 * Source of truth for flags that we use in the app.
 *
 * Values for all flags here will be made available as properties on FlagState.
 * To add a flag, declare its type here and define default values in
 * featureFlagDefinitions.
 *
 * To use a flag reactively:
 * const { myFlagName } = storeToRefs(useFlagStore())
 */
// CONSTANTS --------------------
export const FEATURE_FLAG_TRAFFIC_TYPE = 'user'

export const _featureFlagDefinitions = {
  featureFlagProvider: {
    local: { provider: 'local' },
    fallback: { provider: 'fallback' },
  } as FlagDefinition<ProviderConfig>,
  exampleBooleanFlag: {
    local: true,
    fallback: false,
  },
  exampleStringFlag: {
    local: 'local',
    fallback: 'fallback',
  },
  showOncologyOnlyFilter: {
    // CLEANUP(sc/2100)
    local: true,
    fallback: false,
  },
  eventServiceFocusEvents: {
    // CLEANUP(TEA-1017): remove after event-service dual-write rollout
    local: true,
    fallback: false,
  },
  envAllowsDirectPlaybookAdminEditing: {
    // permanent
    local: true,
    fallback: false,
  },
  showDebugInNavBar: {
    // permanent
    local: true,
    // Set fallback to `true` to help with support debugging in production
    // if FFs aren't working
    fallback: true,
  },
  showInformationBanner: {
    // permanent
    local: undefined,
    //{
    //  message:
    //    'There is a known issue with the queues not loading, which is actively being investigated.',
    //  status: 'INCIDENT',
    //},
    fallback: undefined,
  } as FlagDefinition<InfoBannerConfig>,
  overrideViewAllTasks: {
    // CLEANUP(MT-3618)
    local: true,
    fallback: false,
  },
  // override to allow certain pilot/testing users
  // to use the LLM dev tools without the normal RBAC roles
  overrideUseLlmDevTools: {
    // semi-permanent
    local: true,
    fallback: false,
  },
  useStacksIncident121225: {
    // CLEANUP(MT-3952)
    local: true,
    fallback: false,
  },
  autosaveForms: {
    // CLEANUP(MT-4472)
    local: true,
    fallback: false,
  },
  showExperimentalAIAskSOP: {
    // CLEANUP(HAM-1895)
    local: true,
    fallback: false,
  },
  showCareTeamDocViewer: {
    // CLEANUP(HAM-1895)
    local: true,
    fallback: false,
  },
  showExperimentalAIPatientSummary: {
    // CLEANUP(HAM-1895)
    local: false,
    fallback: false,
  },
  showSmartReattempts: {
    // CLEANUP(MT-4817)
    local: true,
    fallback: false,
  },
  showSchedCallLinkedSubtaskBehavior: {
    // CLEANUP(TEA-1131)
    local: true,
    fallback: false,
  },
  showGoogleCalendar: {
    local: true,
    fallback: false,
  },
  usePlannedCallsStacksView: {
    // CLEANUP(MT-5013)
    local: true,
    fallback: false,
  },
  hideQueueTotal: {
    // CLEANUP(MT-4903)
    local: true,
    fallback: false,
  },
  showTestVpnButton: {
    // permanent
    local: true,
    fallback: false,
  },
  useLongerAutosaveDebounce: {
    // CLEANUP(MT-5100)
    local: true,
    fallback: false,
  },
  useAdditionalSubqueriesStacks: {
    // CLEANUP(MT-5268)
    local: true,
    fallback: false,
  },
  useTemplateFuzzySearch: {
    // CLEANUP(MT-5063)
    local: true,
    fallback: false,
  },
  showCommunicationConsents: {
    local: true,
    fallback: false,
  },
  showExperimentalAIThymelineSummaries: {
    local: true,
    fallback: false,
  },
  showExperimentalNoteTemplates: {
    // CLEANUP(CT-27)
    local: true,
    fallback: false,
  },
  showAskAProfile: {
    // CLEANUP(TEA-240)
    local: true,
    fallback: false,
  },
  showGoalsMemberAgreementAndDate: {
    // CLEANUP(CT-33)
    local: true,
    fallback: false,
  },
  // CLEANUP(SPC-2229)
  showExperimentalRecommendedArticlesForConnectPoc: {
    local: false,
    fallback: false,
  },
  apiUrlOverride: {
    local: { url: 'http://localhost:5001' },
    fallback: undefined,
  } as FlagDefinition<ApiUrlOverrideConfig>,
  useNotificationV2: {
    // CLEANUP(MT-4822)
    local: true,
    fallback: false,
  },
  showSubtaskTriggerOutcomes: {
    // CLEANUP(TEA-261)
    local: true,
    fallback: false,
  },
  usePhoneInput: {
    // CLEANUP(CT-183)
    local: true,
    fallback: false,
  },
  showSelfManagementActivities: {
    // CLEANUP(CT-242)
    local: true,
    fallback: false,
  },
  showAmazonCCP: {
    local: true,
    fallback: false,
  },
  showCcpSoftphoneWindow: {
    // CLEANUP(CT-821)
    local: true,
    fallback: false,
  },
  // CLEANUP(CT-438): auto-reset agents in opted-in routing profiles to
  // Available after a missed call
  resetMissedCallStatus: {
    local: true,
    fallback: false,
  },
  showRestrictedDialWarning: {
    local: true,
    fallback: false,
  },
  useBreakGlassOverlay: {
    // CLEANUP(TEA-509)
    local: false, // keep this off locally for easier testing
    fallback: false,
  },
  showOmaatEnrollmentQueue: {
    // CLEANUP(TEA-534)
    local: true,
    fallback: false,
  },
  enableInboundFaxes: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-924)
  showScheduledCallsQueue: {
    local: true,
    fallback: false,
  },
  // CLEANUP(CT-547)
  enableTutorials: {
    local: false,
    fallback: false,
  },
  // CLEANUP(CT-448)
  viewUnmatchedVoicemails: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1004)
  showSendAcpResource: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1053)
  showNewCancellationSubStatusReasons: {
    local: true,
    fallback: false,
  },
  // CLEANUP(CT-548)
  useProviderCallbacks: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1078)
  textingVueQueryEnabled: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1347): remove after backend SMS send cutover
  // Routes manual SMS sends through the atomic backend endpoint instead of
  // the FE-orchestrated Twilio SDK path. Default off so the cutover lands dark.
  beSmsSendEnabled: {
    local: false,
    fallback: false,
  },
  // CLEANUP(TEA-1073)
  enableTcmAppointmentChip: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1137)
  blockTextingIfMessageHasUhcWords: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1196)
  useStateBasedHieConsentDisplay: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1192)
  showMetriportProfileLink: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1327)
  showStaffSpecializations: {
    local: true,
    fallback: false,
  },
  // CLEANUP(TEA-1308)
  enableHieReciprocity: {
    local: true,
    fallback: false,
  },
  // CLEANUP(MET-2970)
  showScheduledCallsDueDate: {
    local: true,
    fallback: false,
  },
  // CLEANUP(MET-2960)
  showScheduledCallsQuickFixes: {
    local: true,
    fallback: false,
  },
}

/**
 * The feature flag client, plus all of the feature flag properties declared in
 * FeatureFlagDefinition mapped to their Flag types (e.g. boolean or string).
 */
export type FlagState = {
  client?: ThymeFeatureFlagClient<typeof _featureFlagDefinitions> | null
  isLoaded: boolean
} & FeatureFlagValues<typeof _featureFlagDefinitions>
